// ============================================================
// Braval - Results Grid Component
// ============================================================

import React from 'react';
import { Link } from 'react-router-dom';

function formatCurrency(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

function formatDate(value) {
    if (!value) return null;
    return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function DescriptionCell({ title, description, make, model, year, serial, location, dataSource, auctionDate, reportingUser }) {
    const hasTags = make || model || year || serial || location || dataSource || auctionDate || reportingUser;

    return (
        <div className="description-cell">
            {title && (
                <div className="description-title">{title}</div>
            )}
            {description && (
                <div className="description-text">{description}</div>
            )}
            {hasTags && (
                <div className="description-tags">
                    {make && (
                        <span className="description-tag">
                            <span className="tag-label">Make</span><strong>{make}</strong>
                        </span>
                    )}
                    {model && (
                        <span className="description-tag">
                            <span className="tag-label">Model</span><strong>{model}</strong>
                        </span>
                    )}
                    {year && (
                        <span className="description-tag">
                            <span className="tag-label">Year</span><strong>{year}</strong>
                        </span>
                    )}
                    {serial && (
                        <span className="description-tag">
                            <span className="tag-label">S/N</span><strong>{serial}</strong>
                        </span>
                    )}
                    {location && (
                        <span className="description-tag">
                            <span className="tag-label">Location</span><strong>{location}</strong>
                        </span>
                    )}
                    {dataSource && (
                        <span className="description-tag">
                            <span className="tag-label">Source</span><strong>{dataSource}</strong>
                        </span>
                    )}
                    {auctionDate && (
                        <span className="description-tag">
                            <span className="tag-label">Auction Date</span><strong>{formatDate(auctionDate)}</strong>
                        </span>
                    )}
                    {reportingUser && (
                        <span className="description-tag">
                            <span className="tag-label">Reported By</span><strong>{reportingUser}</strong>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ResultsGrid({ results, loading, hasSearched, error }) {

    if (loading) {
        return (
            <div className="results-container">
                <div className="results-status">
                    <span className="loading-spinner"></span>
                    Searching...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="results-container">
                <div className="results-status results-error">
                    {error}
                </div>
            </div>
        );
    }

    if (!hasSearched) {
        return (
            <div className="results-container">
                <div className="results-status">
                    Enter a search term or apply filters to find auction comps.
                </div>
            </div>
        );
    }

    if (results.length === 0) {
        return (
            <div className="results-container">
                <div className="results-status">
                    No results found. Try adjusting your search or filters.
                </div>
            </div>
        );
    }

    return (
        <div className="results-container">
            <div className="results-count">
                {results.length} result{results.length !== 1 ? 's' : ''} found
                {results.length === 500 && (
                    <span className="results-cap"> — showing first 500, consider refining your search</span>
                )}
            </div>
            <table className="results-table">
                <thead>
                    <tr>
                        <th className="col-lot">Lot #</th>
                        <th className="col-description">Description</th>
                        <th className="col-price">Sale Price</th>
                        <th className="col-project">Project</th>
                        <th className="col-auctioneer">Auctioneer</th>
                        <th className="col-actions"></th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((row) => (
                        <tr key={row.lot_id}>
                            <td className="col-lot">{row.lot_number || '—'}</td>
                            <td className="col-description">
                                <DescriptionCell
                                    title={row.title}
                                    description={row.description}
                                    make={row.make}
                                    model={row.model}
                                    year={row.year}
                                    serial={row.serial_number}
                                    location={row.location_region}
                                    dataSource={row.data_source}
                                    auctionDate={row.auction_date}
                                    reportingUser={row.reporting_user}
                                />
                            </td>
                            <td className="col-price sale-price">{formatCurrency(row.sale_price)}</td>
                            <td className="col-project">{row.project_name || '—'}</td>
                            <td className="col-auctioneer">{row.auctioneer_firm || '—'}</td>
                            <td className="col-actions">
                                <Link to={`/lots/${row.lot_id}/edit`} className="search-button row-edit-button">Edit</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}